import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AuthPage from './components/AuthPage';
import ChatInterface from './components/ChatInterface';
import EmailVerificationPrompt from './components/EmailVerificationPrompt';
import HomePage from './components/HomePage';
import { auth } from './config/firebase';

// This component now holds the logic from your original App.jsx
function MainApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // CRITICAL: Only set the user if they are NOT anonymous
      // Anonymous users will be handled by the SurveyPage
      if (currentUser && !currentUser.isAnonymous) {
        // Reload user to get fresh email verification status
        await currentUser.reload();
        setUser(currentUser);
      } else {
        // If user is null or anonymous, treat them as logged out
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [refreshKey]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
      // setUser(null) will be handled by onAuthStateChanged
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleRefresh = () => {
    // Increment refreshKey to trigger useEffect and reload user state
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-nova-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-primary rounded-full mx-auto mb-4 flex items-center justify-center animate-pulse">
            <div className="w-8 h-8 bg-white rounded-full"></div>
          </div>
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Home page - promotional landing, redirects to app if signed in */}
      <Route
        path="/"
        element={
          user ? (
            <Navigate to="/app" replace />
          ) : (
            <HomePage />
          )
        }
      />

      {/* Authentication page */}
      <Route
        path="/auth"
        element={
          user ? (
            <Navigate to="/app" replace />
          ) : (
            <AuthPage />
          )
        }
      />

      {/* App routes - protected */}
      <Route
        path="/app"
        element={
          user ? (
            user.emailVerified ? (
              <ChatInterface user={user} onSignOut={handleSignOut} />
            ) : (
              <EmailVerificationPrompt
                user={user}
                onSignOut={handleSignOut}
                onRefresh={handleRefresh}
              />
            )
          ) : (
            <Navigate to="/auth" replace />
          )
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default MainApp;